import {
  isWebView,
  requestFcmToken,
  requestDeviceInfo,
  checkNotificationPermission,
  requestNotificationPermission
} from './webViewBridge'
import { registerFcmTokenApi } from '@/api/notificationApi'
import { useUserStore } from '@/store/userStore'
import { useAuthStore } from '@/store/authStore'
import { getUserIdFromToken } from './jwt'
import { LOCAL_STORAGE_KEYS, safeLocalStorage } from '@/constants/storageKeys'
import { logger } from './logger'

// FCM 토큰 관리 결과 타입
export interface FcmTokenResult {
  success: boolean
  message: string
  token?: string
  permissionGranted?: boolean
}

/**
 * 로그인 완료 후 FCM 토큰을 자동으로 요청하고 백엔드에 저장합니다.
 * 웹뷰 환경에서만 작동하며, 브라우저 환경에서는 무시됩니다.
 *
 * @param userId - 사용자 ID
 * @param forceUpdate - 강제 업데이트 여부 (기본: false)
 * @returns FCM 토큰 처리 결과
 */
export const initializeFcmToken = async (userId: number, forceUpdate: boolean = false): Promise<FcmTokenResult> => {
  logger.fcm('FCM 토큰 초기화 시작', { userId, forceUpdate, isWebView: isWebView() })

  // 웹뷰 환경이 아닌 경우 무시
  if (!isWebView()) {
    logger.fcm('웹뷰 환경이 아님 - FCM 토큰 초기화 건너뜀')
    return {
      success: false,
      message: '웹뷰 환경이 아닙니다'
    }
  }

  try {
    // 1. 알림 권한 확인 (권한이 없어도 FCM 토큰과 기기정보는 저장)
    logger.fcm('알림 권한 확인 중...')
    let permissionGranted = false

    try {
      const hasPermission = await checkNotificationPermission()

      if (!hasPermission) {
        logger.fcm('알림 권한이 없음 - 권한 요청 중...')
        permissionGranted = await requestNotificationPermission()

        if (!permissionGranted) {
          logger.warn('알림 권한 요청 거부됨 - 그래도 FCM 토큰과 기기정보는 저장합니다')
        } else {
          logger.success('알림 권한 승인됨')
        }
      } else {
        logger.success('알림 권한 이미 승인됨')
        permissionGranted = true
      }
    } catch (permissionError) {
      logger.warn('알림 권한 확인/요청 중 오류 발생:', permissionError)
      logger.fcm('권한 오류에도 불구하고 FCM 토큰 수집을 계속 진행합니다')
    }

    // 2. FCM 토큰 요청 (권한 상태와 무관하게 진행)
    logger.fcm('FCM 토큰 요청 중...')
    let fcmTokenData = null
    let fcmTokenError = null

    try {
      fcmTokenData = await requestFcmToken()
    } catch (error) {
      fcmTokenError = error
      logger.warn('FCM 토큰 요청 중 오류 발생:', error)
    }

    // 3. 디바이스 정보 요청 (FCM 토큰 실패와 무관하게 진행)
    logger.fcm('디바이스 정보 요청 중...')
    let deviceInfo = null

    try {
      deviceInfo = await requestDeviceInfo()
      logger.success('디바이스 정보 획득 성공')
    } catch (error) {
      logger.warn('디바이스 정보 요청 중 오류 발생:', error)
    }

    // 4. 기기정보라도 백엔드에 저장 (FCM 토큰이 없어도 진행)
    if (fcmTokenData?.token) {
      logger.fcm('FCM 토큰 획득 성공', {
        token: fcmTokenData.token.substring(0, 20) + '...',
        platform: fcmTokenData.platform
      })

      logger.fcm('백엔드에 FCM 토큰 저장 중...')
      const saveResult = await registerFcmTokenApi({
        userId,
        fcmToken: fcmTokenData.token,
        deviceInfo: {
          platform: fcmTokenData.platform,
          model: deviceInfo?.model || 'Unknown',
          appVersion: deviceInfo?.appVersion || '1.0.0'
        },
        notificationSettings: {
          common: permissionGranted,
          marketing: permissionGranted,
          consultation: permissionGranted
        }
      })

      if (saveResult.code === '200' && saveResult.data?.success) {
        logger.success('FCM 토큰 저장 성공')
        return {
          success: true,
          message: permissionGranted
            ? 'FCM 토큰이 성공적으로 등록되었습니다'
            : 'FCM 토큰이 등록되었습니다 (알림 권한은 거부됨)',
          token: fcmTokenData.token,
          permissionGranted
        }
      } else {
        logger.error('FCM 토큰 저장 실패:', saveResult.message)
        return {
          success: false,
          message: `FCM 토큰 저장 실패: ${saveResult.message}`,
          token: fcmTokenData.token,
          permissionGranted
        }
      }
    } else {
      // FCM 토큰을 얻지 못했지만 기기정보라도 저장 시도
      logger.warn('FCM 토큰을 얻지 못했지만 기기정보는 저장합니다')

      if (deviceInfo) {
        logger.fcm('백엔드에 기기정보만 저장 중...')
        try {
          const saveResult = await registerFcmTokenApi({
            userId,
            fcmToken: null, // FCM 토큰 없이 저장
            deviceInfo: {
              platform: deviceInfo.platform || 'Unknown',
              model: deviceInfo.model || 'Unknown',
              appVersion: deviceInfo.appVersion || '1.0.0'
            },
            notificationSettings: {
              common: false,
              marketing: false,
              consultation: false
            }
          })

          if (saveResult.code === '200' && saveResult.data?.success) {
            logger.success('기기정보 저장 성공 (FCM 토큰 없음)')
            return {
              success: true,
              message: '기기정보가 저장되었습니다 (FCM 토큰 없음)',
              permissionGranted
            }
          } else {
            logger.error('기기정보 저장 실패:', saveResult.message)
          }
        } catch (error) {
          logger.warn('기기정보 저장 중 오류:', error)
        }
      }

      return {
        success: false,
        message: fcmTokenError
          ? `FCM 토큰 요청 실패: ${fcmTokenError instanceof Error ? fcmTokenError.message : 'Unknown error'}`
          : 'FCM 토큰을 가져올 수 없습니다',
        permissionGranted
      }
    }
  } catch (error) {
    logger.error('FCM 토큰 초기화 중 오류 발생:', error)
    return {
      success: false,
      message: `FCM 토큰 초기화 실패: ${error instanceof Error ? error.message : 'Unknown error'}`
    }
  }
}

/**
 * FCM 토큰을 서버에서 삭제합니다.
 * 로그아웃이나 회원탈퇴 시 호출됩니다.
 */
export const removeFcmToken = async (): Promise<void> => {
  logger.fcm('FCM 토큰 삭제 시작...')

  // 웹뷰 환경이 아니면 처리하지 않음
  if (!isWebView()) {
    logger.fcm('웹뷰 환경이 아님 - FCM 토큰 삭제 건너뜀')
    return
  }

  const userStore = useUserStore.getState()
  const userInfo = userStore.userInfo

  if (!userInfo?.user_id) {
    logger.warn('사용자 정보가 없어 FCM 토큰 삭제를 건너뜁니다')
    return
  }

  try {
    // 디바이스 정보 가져오기
    const deviceInfo = await requestDeviceInfo()

    // FCM 토큰을 null로 설정하여 서버에서 삭제
    const result = await registerFcmTokenApi({
      userId: userInfo.user_id,
      fcmToken: null, // null로 설정하여 삭제 요청
      deviceInfo: {
        platform: deviceInfo?.platform || 'Unknown',
        model: deviceInfo?.model || 'Unknown',
        appVersion: deviceInfo?.appVersion || '1.0.0'
      },
      notificationSettings: {
        common: false,
        marketing: false,
        consultation: false
      }
    })

    if (result.code === '200' && result.data?.success) {
      logger.success('FCM 토큰 삭제 성공')
      // 로컬 스토리지의 마지막 업데이트 시간도 삭제
      safeLocalStorage.remove(LOCAL_STORAGE_KEYS.getFCMUpdateKey(userInfo.user_id))
    } else {
      logger.warn('FCM 토큰 삭제 실패:', result.message)
    }
  } catch (error) {
    logger.error('FCM 토큰 삭제 중 오류:', error)
  }
}

/**
 * 저장된 FCM 토큰이 있는지 확인하고 필요시 업데이트합니다.
 * localStorage에 마지막 토큰 업데이트 시간을 저장하여 중복 요청을 방지합니다.
 *
 * @param userId - 사용자 ID
 * @param maxAge - 토큰의 최대 유효 시간 (밀리초, 기본: 24시간)
 * @returns FCM 토큰 처리 결과
 */
export const ensureFcmTokenUpdated = async (
  userId: number,
  maxAge: number = 24 * 60 * 60 * 1000
): Promise<FcmTokenResult> => {
  const storageKey = LOCAL_STORAGE_KEYS.getFCMUpdateKey(userId)
  const lastUpdate = safeLocalStorage.get(storageKey)

  // 마지막 업데이트로부터 충분한 시간이 지나지 않았으면 건너뜀
  if (lastUpdate) {
    const lastUpdateTime = parseInt(lastUpdate, 10)
    const timeSinceUpdate = Date.now() - lastUpdateTime

    if (timeSinceUpdate < maxAge) {
      logger.fcm('FCM 토큰이 최근에 업데이트됨 - 건너뜀', {
        timeSinceUpdate: Math.round(timeSinceUpdate / (60 * 1000)),
        maxAgeMinutes: Math.round(maxAge / (60 * 1000))
      })
      return {
        success: true,
        message: 'FCM 토큰이 최근에 업데이트되었습니다'
      }
    }
  }

  // FCM 토큰 초기화 실행
  const result = await initializeFcmToken(userId)

  // 성공한 경우 마지막 업데이트 시간 저장
  if (result.success) {
    safeLocalStorage.set(storageKey, Date.now().toString())
  }

  return result
}

/**
 * 로그인 성공 시 자동으로 FCM 토큰을 초기화하는 헬퍼 함수
 */
export const handlePostLoginFcmSetup = async (): Promise<void> => {
  logger.fcm('로그인 후 FCM 설정 시작...')

  // 1. Zustand store 확인
  const userStore = useUserStore.getState()
  let userId = userStore.userInfo?.user_id

  logger.fcm('Zustand store user_id:', userId)

  // 2. Zustand store에 user_id가 없으면 토큰에서 추출 시도
  if (!userId || userId === 0) {
    logger.fcm('Zustand store에 user_id가 없어 토큰에서 추출 시도...')
    const accessToken = useAuthStore.getState().getAccessToken()
    if (accessToken) {
      userId = getUserIdFromToken(accessToken) || -1
      logger.fcm('토큰에서 user_id 추출:', userId)
    }
  }

  if (!userId || userId === 0) {
    logger.warn('사용자 ID를 찾을 수 없어 FCM 초기화를 건너뜁니다')
    return
  }

  logger.fcm('최종 user_id:', userId)

  // 비동기로 FCM 토큰 초기화 실행 (로그인 플로우를 블로킹하지 않음)
  setTimeout(async () => {
    try {
      const result = await ensureFcmTokenUpdated(userId)
      logger.fcm('로그인 후 FCM 초기화 완료:', result.message)
    } catch (error) {
      logger.error('로그인 후 FCM 초기화 실패:', error)
    }
  }, 1000) // 1초 후 실행하여 로그인 완료 후 안정적으로 처리
}
